import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { chatsApi } from "@/api/chats.ts";
import { teamApi } from "@/api/studio.ts";
import { TeamRoster, useTeamActivity } from "@/features/chat/TeamRoster.tsx";
import { captureBus } from "@/features/projects/ProjectPage.tsx";
import { Alert } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";

/** Project team in the side panel: tap a member to mention it, tick its box to pin it to the open chat. */
export function TeamPanel({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const { chatId } = useParams({ strict: false }) as { chatId?: string };
  const q = useQuery({ queryKey: ["mentionables", projectId], queryFn: () => teamApi.mentionables(projectId) });
  // the open chat itself arrives over its socket in ChatPage; here only the pin is needed, refreshed after a change
  const chat = useQuery({ queryKey: ["chats", projectId, "list"], queryFn: () => chatsApi.list(projectId), enabled: !!chatId, select: (list) => list.find((c) => c.id === chatId) });
  const activity = useTeamActivity(chatId);
  const pin = useMutation({ mutationFn: (handle: string | null) => chatsApi.update(chatId!, { pinnedAgentHandle: handle }), onSuccess: () => void chat.refetch() });
  const insert = (handle: string) => captureBus.dispatchEvent(new CustomEvent("mention", { detail: handle }));
  return (
    <div className="flex h-full flex-col">
      <p className="border-b border-border px-4 py-2 text-xs text-fg-muted">{chatId ? t("team.panelHint") : t("team.panelHintNoChat")}</p>
      <div className="min-h-0 flex-1 space-y-2 overflow-auto p-3">
        {pin.error && <Alert tone="danger">{toMsg(pin.error)}</Alert>}
        <TeamRoster members={q.data ?? []} pinned={chat.data?.pinnedAgentHandle ?? null} activity={activity} canPin={!!chatId && !pin.isPending} onMention={insert} onTogglePin={(h) => pin.mutate(h)} />
      </div>
    </div>
  );
}
